import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { PettyCashFloatListRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { todayIso } from './pc-helpers';
import { useCoa } from '../../lib/queries/coa';
import { useCreatePettyCashClose } from '../../lib/queries/petty-cash';
import { SearchSelect } from '../SearchSelect';

// ============================================================
// Float-close modal (PCX) — return the residual cash to a bank account and
// close the float. Enters the PCX approval chain (2 steps, FINANCE_MANAGER →
// CFO); the return JV posts + the float flips CLOSED on final approval.
// Reuses the same bank SearchSelect idiom as the float-open / replenish modal.
// ============================================================

interface PCCloseModalProps {
  companyId: string | undefined;
  liveFloats: readonly PettyCashFloatListRow[];
  initialFloatId?: string | undefined;
  onClose: () => void;
}

export function PCCloseModal({
  companyId,
  liveFloats,
  initialFloatId,
  onClose,
}: PCCloseModalProps) {
  const activeFloats = useMemo(() => liveFloats.filter((f) => f.status === 'ACTIVE'), [liveFloats]);
  const [floatId, setFloatId] = useState<string>(initialFloatId ?? activeFloats[0]?.id ?? '');
  const [closeDate, setCloseDate] = useState<string>(todayIso());
  const [custodyNotes, setCustodyNotes] = useState<string>('');
  const { data: coa = [] } = useCoa(companyId, true);
  const bankAccounts = useMemo(
    // Same 8-digit cash & bank-deposit group (101xxxxx, non-control) as the
    // float-open / replenish modals.
    () =>
      coa.filter(
        (a) => a.type === 'ASSET' && a.isActive && !a.isControl && a.code.startsWith('101'),
      ),
    [coa],
  );
  const defaultBankCode = useMemo(
    () => (bankAccounts.find((a) => a.code.startsWith('1012')) ?? bankAccounts[0])?.code ?? '',
    [bankAccounts],
  );
  const [toAccountCode, setToAccountCode] = useState<string>('');
  const resolvedToAccount = toAccountCode || defaultBankCode;
  const { mutate: createClose, isPending, error } = useCreatePettyCashClose(companyId);

  const selected = activeFloats.find((f) => f.id === floatId);
  const currentBalance = selected ? Number(selected.currentBalance) : 0;
  // returnedAmount defaults to the float's currentBalance; the user may override.
  const [returnedText, setReturnedText] = useState<string>('');
  const returnedAmount = returnedText.trim() ? Number(returnedText) : currentBalance;

  function handleSubmit() {
    if (!floatId || !resolvedToAccount) return;
    if (!Number.isFinite(returnedAmount) || returnedAmount < 0) return;
    createClose(
      {
        data: {
          floatId,
          closeDate,
          returnedAmount,
          toAccountCode: resolvedToAccount,
          ...(custodyNotes.trim() ? { custodyNotes: custodyNotes.trim() } : {}),
        },
        idempotencyKey: crypto.randomUUID(),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pc-close-modal"
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ปิด Float (Close-out)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              คืนเงินสดคงเหลือเข้าบัญชีธนาคาร แล้วปิด float (เข้าสายอนุมัติ 2 ขั้น)
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              Float สาขา
            </label>
            <select
              className="select"
              value={floatId}
              onChange={(e) => setFloatId(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
              disabled={activeFloats.length === 0}
            >
              {activeFloats.length === 0 && <option value="">— ไม่มี float ที่ active —</option>}
              {activeFloats.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.branchName} · คงเหลือ {fmt(Number(f.currentBalance))} บาท
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ปิด
              </label>
              <input
                className="input"
                type="date"
                value={closeDate}
                onChange={(e) => setCloseDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                เงินคืน (บาท)
              </label>
              <input
                className="input tnum"
                data-testid="pc-close-returned"
                type="number"
                step="0.01"
                min="0"
                placeholder={fmt(currentBalance)}
                value={returnedText}
                onChange={(e) => setReturnedText(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 16,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              บัญชีปลายทาง (ธนาคาร)
            </label>
            <SearchSelect
              value={resolvedToAccount}
              onChange={setToAccountCode}
              placeholder="— เลือกบัญชีธนาคาร —"
              options={bankAccounts.map((a) => ({
                value: a.code,
                label: `${a.code} — ${a.nameTh}`,
              }))}
              getLabel={(v) => {
                const a = bankAccounts.find((x) => x.code === v);
                return a ? `${a.code} — ${a.nameTh}` : '';
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              บันทึกการส่งมอบ (Custody notes)
            </label>
            <input
              className="input"
              placeholder="เช่น ส่งมอบเงินสด/เอกสารคืนแผนกการเงินครบถ้วน"
              value={custodyNotes}
              onChange={(e) => setCustodyNotes(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontSize: 14,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span className="muted">คงเหลือใน float ปัจจุบัน</span>
            <span style={{ fontWeight: 600, fontFamily: 'var(--font-display)' }} className="tnum">
              {fmt(currentBalance)} บาท
            </span>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--negative-bg)',
                borderRadius: 8,
                border: '1px solid var(--negative)',
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              {error.message}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isPending}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            data-testid="pc-close-submit"
            onClick={handleSubmit}
            disabled={isPending || !floatId || !resolvedToAccount}
          >
            <Icon name="check" size={14} /> {isPending ? 'กำลังบันทึก…' : 'ยื่นขอปิด float'}
          </button>
        </div>
      </div>
    </div>
  );
}

export type { PCCloseModalProps };
